import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Divider, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MenuIcon from '@mui/icons-material/Menu';
import { logout } from '../../store/slices/authSlice';
import NotificationBell from './NotificationBell';
import { roleColors, initialsFromName } from '../../theme/roleMockup';
import { useActiveProject } from '../../context/ActiveProjectContext';
import { buildProjectUrl, isStaffRole } from '../../utils/deepLink';

export default function RoleTopBar({ onMenuClick }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const isClient = user?.role === 'client' || user?.role === 'client_team';
  const isStaff = isStaffRole(user?.role);
  const usesProjectSwitcher = isClient || isStaff;
  const { projects, activeProjectId, setActiveProject, activeProject, loading: projectsLoading } =
    useActiveProject();

  const displayName =
    user?.companyName ||
    user?.organizationName ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.email ||
    'User';

  const handleLogout = () => {
    setMenuAnchor(null);
    if (user?.role) localStorage.setItem('lastUserRole', user.role);
    dispatch(logout());
  };

  const handleLogoClick = () => {
    if (usesProjectSwitcher) {
      if (activeProjectId) {
        navigate(buildProjectUrl(activeProjectId, 'dashboard'));
      } else {
        navigate(isStaff ? '/app/dashboard' : '/app/projects');
      }
      return;
    }
    navigate('/app/dashboard');
  };

  const switchProject = (projectId) => {
    setMenuAnchor(null);
    setActiveProject(projectId);
    navigate(buildProjectUrl(projectId, 'dashboard'));
  };

  return (
    <Box
      component="header"
      sx={{
        height: 'calc(52px + env(safe-area-inset-top, 0px))',
        pt: 'env(safe-area-inset-top, 0px)',
        boxSizing: 'border-box',
        flexShrink: 0,
        bgcolor: roleColors.topbar,
        display: 'flex',
        alignItems: 'center',
        px: { xs: 1.5, sm: 2.5 },
        gap: { xs: 1, sm: 2 },
        zIndex: 100,
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      {typeof onMenuClick === 'function' && (
        <IconButton
          onClick={onMenuClick}
          aria-label="Open navigation"
          size="small"
          sx={{
            display: { xs: 'inline-flex', md: 'none' },
            color: '#fff',
            p: 0.75,
            flexShrink: 0,
          }}
        >
          <MenuIcon fontSize="small" />
        </IconButton>
      )}

      <Box
        component="span"
        onClick={handleLogoClick}
        sx={{
          fontSize: { xs: 16, sm: 17 },
          fontWeight: 800,
          color: '#fff',
          letterSpacing: '-0.5px',
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        Test<span style={{ color: '#3b82f6' }}>Gate</span>
      </Box>

      {usesProjectSwitcher && activeProject?.title && (
        <Typography
          sx={{
            display: { xs: 'none', sm: 'block' },
            fontSize: 12,
            fontWeight: 600,
            color: '#94a3b8',
            maxWidth: { sm: 160, md: 280 },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {activeProject.title}
        </Typography>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 }, ml: 'auto', minWidth: 0, flexShrink: 0 }}>
        <NotificationBell dark />
        <Box
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            color: '#cbd5e1',
            fontSize: 12,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              bgcolor: '#3b82f6',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {initialsFromName(displayName)}
          </Box>
          <Box
            component="span"
            sx={{
              display: { xs: 'none', sm: 'block' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: { sm: 160, md: 220 },
            }}
          >
            {displayName}
          </Box>
          <KeyboardArrowDownIcon sx={{ fontSize: 16, color: '#94a3b8', display: { xs: 'none', sm: 'block' } }} />
        </Box>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          PaperProps={{ sx: { minWidth: 220, maxWidth: 320 } }}
        >
          {usesProjectSwitcher && projects.length > 0 && (
            <>
              <Typography
                sx={{ px: 2, py: 1, fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}
              >
                Switch project
              </Typography>
              {projects.map((p) => (
                <MenuItem
                  key={p.id}
                  selected={p.id === activeProjectId}
                  disabled={projectsLoading}
                  onClick={() => switchProject(p.id)}
                >
                  {p.title}
                </MenuItem>
              ))}
              {projects.length > 1 && (
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    setActiveProject('');
                    navigate('/app/projects');
                  }}
                >
                  Choose another project…
                </MenuItem>
              )}
              <Divider sx={{ my: 0.5 }} />
            </>
          )}
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              navigate('/app/settings');
            }}
          >
            Settings
          </MenuItem>
          <MenuItem onClick={handleLogout}>Sign out</MenuItem>
        </Menu>
      </Box>
    </Box>
  );
}
